/**
 * Формулы в ответе модели: TeX → готовый HTML (KaTeX).
 *
 * Зачем это существует. Модели пишут формулы в TeX (`$$…$$`, `\[…\]`,
 * `\(…\)`, `$…$`), а markdown про TeX ничего не знает: для него `\[` — это
 * экранированная скобка, `_` — курсив, а пара слэшей — один слэш. Поэтому
 * читателю доставались обломки разметки вместо формулы:
 *
 *     [ \text{проблема учеников} \rightarrow \text{метод} ]
 *
 * Запретить TeX промптом нельзя: категории вроде «Number» прямо требуют
 * «покажи формулы, преобразования и промежуточные результаты», — значит TeX
 * надо отображать, а не вычищать.
 *
 * Принципиален здесь ПОРЯДОК операций, и только он:
 *
 *   1. формулы вырезаются ДО markdown — иначе разметка успевает их испортить;
 *   2. на их месте остаётся метка, которую не трогают ни marked, ни DOMPurify;
 *   3. HTML формулы подставляется ПОСЛЕ санитайзера — KaTeX отдаёт MathML,
 *      и DOMPurify перекраивает его в нечитаемое.
 *
 * Безопасность подстановки после санитайзера держится на том, что HTML здесь
 * не пользовательский: KaTeX собирает его сам и экранирует всё, что пришло из
 * текста (`trust` по умолчанию выключен, никаких `\href` и `\htmlClass`).
 * Не разобравшаяся формула возвращается в текст экранированной — ровно тем,
 * чем была до этого файла.
 *
 * Стили: вендорный `katex.min.css` подключён в shared/vendor-globals.ts,
 * правки под наш макет — в vars.css (секция «Формулы»).
 *
 * KaTeX грузится ЛЕНИВО и только вместе с первой формулой: статический импорт
 * клал 260 КБ в чанк `ui`, который приезжает при каждом заходе, тогда как
 * большинство разговоров формул не содержит вовсе. До приезда библиотеки
 * формула стоит в разметке заглушкой с исходным TeX внутри, а приехавшая
 * библиотека проходит по документу и заменяет заглушки (`upgradePendingMath`).
 */

type KatexApi = {
  renderToString(tex: string, options: Record<string, unknown>): string;
};

let katex: KatexApi | null = null;
let katexLoading: Promise<void> | null = null;

/**
 * Грузит KaTeX один раз. Сорвавшаяся загрузка обнуляет обещание: следующая
 * формула попробует снова, а до тех пор в тексте стоит исходный TeX — то же,
 * что видел читатель до появления этого файла.
 */
export function ensureKatex(): Promise<void> {
  if (!katexLoading) {
    katexLoading = import('katex')
      .then((module) => {
        katex = ((module as { default?: KatexApi }).default ?? module) as KatexApi;
        upgradePendingMath();
      })
      .catch(() => {
        katexLoading = null;
      });
  }
  return katexLoading;
}

export interface MathSpan {
  /** Тело формулы без ограничителей — то, что понимает KaTeX. */
  source: string;
  /** Исходный фрагмент целиком: он вернётся в текст, если формула не разобралась. */
  raw: string;
  /** `\[…\]` и `$$…$$` — выключная формула, отдельным блоком. */
  display: boolean;
}

export interface ExtractedMath {
  /** Текст, в котором формулы заменены метками. */
  text: string;
  spans: MathSpan[];
}

/**
 * Метка обрамлена U+2063 (INVISIBLE SEPARATOR): в тексте от модели такого
 * символа не бывает, markdown его не трогает, DOMPurify оставляет как текст.
 */
const MARK = '\u2063';
const PLACEHOLDER_RE = new RegExp(`${MARK}math(\\d+)${MARK}`, 'g');
const CODE_PLACEHOLDER_RE = new RegExp(`${MARK}code(\\d+)${MARK}`, 'g');

/**
 * Код вырезается ОТДЕЛЬНЫМ проходом и до формул. Одним чередованием это не
 * решается: в строке «цена $5 и $10 за урок, а `$x$` в скрипте» сканер доходит
 * до `$10` раньше, чем до кода, и дотягивает пару до доллара ВНУТРИ кода —
 * получается формула из половины предложения. Спрятанный код такой пары не даёт.
 */
const CODE = /```[\s\S]*?```|~~~[\s\S]*?~~~|`[^`\n]*`/g;

/** Ограничители формул. Порядок внутри чередования значения не имеет. */
const MATH =
  /\$\$[\s\S]+?\$\$|\\\[[\s\S]+?\\\]|\\\([\s\S]+?\\\)|\$[^\n$]+?\$/g;

function placeholder(index: number): string {
  return `${MARK}math${index}${MARK}`;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/** Нечётное число слэшей перед началом — ограничитель экранирован автором. */
function isEscaped(text: string, start: number): boolean {
  let slashes = 0;
  for (let i = start - 1; i >= 0 && text[i] === '\\'; i -= 1) slashes += 1;
  return slashes % 2 === 1;
}

/**
 * Одиночный `$` — единственный ограничитель, который сталкивается с обычным
 * текстом: «цена $5 и $10» формулой не является. Правило то же, что у
 * markdown-it-katex: тело без пробелов по краям и цифра сразу за закрывающим
 * `$` запрещена.
 */
function looksLikeInlineMath(source: string, text: string, end: number): boolean {
  if (source.trim() === '') return false;
  if (/^\s|\s$/.test(source)) return false;
  return !/\d/.test(text[end] ?? '');
}

/** Прячет код за метками — сканер формул внутрь них не заглядывает. */
function maskCode(text: string): { text: string; code: string[] } {
  const code: string[] = [];
  const masked = text.replace(CODE, (fragment) => {
    code.push(fragment);
    return `${MARK}code${code.length - 1}${MARK}`;
  });
  return { text: masked, code };
}

function unmaskCode(text: string, code: readonly string[]): string {
  if (code.length === 0) return text;
  return text.replace(CODE_PLACEHOLDER_RE, (whole, index: string) => code[Number(index)] ?? whole);
}

/**
 * Вырезает формулы, оставляя вместо них метки. Незакрытая формула (поток ещё
 * идёт, `$$` пришло, а пары ещё нет) не совпадает ни с чем и остаётся текстом —
 * следующий кадр стрима покажет её уже формулой.
 */
export function extractMathSpans(source: string): ExtractedMath {
  const spans: MathSpan[] = [];
  const { text: masked, code } = maskCode(source);
  let out = '';
  let cursor = 0;

  MATH.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = MATH.exec(masked)) !== null) {
    const raw = match[0];
    const start = match.index;

    if (isEscaped(masked, start)) continue;

    let body: string;
    let display: boolean;
    if (raw.startsWith('$$')) {
      body = raw.slice(2, -2);
      display = true;
    } else if (raw.startsWith('\\[')) {
      body = raw.slice(2, -2);
      display = true;
    } else if (raw.startsWith('\\(')) {
      body = raw.slice(2, -2);
      display = false;
    } else {
      body = raw.slice(1, -1);
      display = false;
      if (!looksLikeInlineMath(body, masked, MATH.lastIndex)) {
        // Закрывающий `$` мог быть открывающим у настоящей формулы следом.
        MATH.lastIndex = start + raw.length - 1;
        continue;
      }
    }
    if (body.trim() === '') continue;

    out += masked.slice(cursor, start) + placeholder(spans.length);
    cursor = start + raw.length;
    spans.push({
      source: unmaskCode(body, code),
      raw: unmaskCode(raw, code),
      display,
    });
  }

  return { text: unmaskCode(out + masked.slice(cursor), code), spans };
}

/**
 * Формула → HTML. Библиотека ещё едет — на её место встаёт заглушка с исходным
 * TeX; не разобралась — вернётся исходным текстом, как было раньше.
 */
export function renderMathSpan(span: MathSpan): string {
  if (!katex) {
    void ensureKatex();
    return (
      `<span class="math-pending" data-math="${escapeHtml(span.source)}"` +
      ` data-display="${span.display ? '1' : '0'}">${escapeHtml(span.raw || span.source)}</span>`
    );
  }
  try {
    return katex.renderToString(span.source, {
      displayMode: span.display,
      throwOnError: true,
      // Кириллица в `\text{}` для KaTeX — «нестрогий» юникод; это норма ответа
      // на русском, а не повод отказаться рисовать формулу.
      strict: false,
    });
  } catch {
    return `<span class="math-raw">${escapeHtml(span.raw)}</span>`;
  }
}

/** Возвращает формулы на места меток. Вызывать ПОСЛЕ санитайзера. */
export function restoreMathSpans(html: string, spans: readonly MathSpan[]): string {
  if (spans.length === 0) return html;
  return html.replace(PLACEHOLDER_RE, (whole, index: string) => {
    const span = spans[Number(index)];
    return span ? renderMathSpan(span) : whole;
  });
}

/**
 * Заменяет заглушки на отрисованные формулы. Проход идёт по всему документу, а
 * не по одному сообщению: заглушку мог оставить любой вызов `renderMarkdownHtml`,
 * в том числе тот, что вернул строку и вставил её сам (аннотации «Видео»).
 * Дожидаться сообщения от каждого из них дороже, чем один querySelectorAll.
 */
export function upgradePendingMath(root?: ParentNode): void {
  if (!katex) return;
  const scope = root ?? (typeof document === 'undefined' ? null : document);
  if (!scope) return;

  for (const node of Array.from(scope.querySelectorAll('.math-pending'))) {
    const el = node as HTMLElement;
    const source = el.getAttribute('data-math') ?? '';
    if (!source) continue;
    const html = renderMathSpan({
      source,
      raw: el.textContent ?? source,
      display: el.getAttribute('data-display') === '1',
    });
    const holder = document.createElement('span');
    holder.innerHTML = html;
    el.replaceWith(...Array.from(holder.childNodes));
  }
}
